import React, { useCallback, useMemo } from 'react'
import Pane from 'components/Common/Pane'
import { rem } from 'polished'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import OfferBookButton from '../OfferBookButtons/OfferBookButton'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCheapestSelectedPackageOptionWithDefault, getFromDurationLabel } from 'selectors/offerPage/offerPageSelectors'
import Group from 'components/utils/Group'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { DAY_FORMAT, DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import useVillasPrice from './useVillasPrice'
import FilterChip from 'LuxKit/Chips/FilterChip'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import moment from 'moment'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorVillasOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'

const Footer = styled(Pane)`
  padding: ${rem(20)};
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  z-index: ${zIndex.stickyFooter};
  box-shadow: ${props => props.theme.shadow.top.small};
`

interface Props {
  packageView: App.OfferPackageOptionView
  durationLabel: string
  rooms?: Array<App.Occupants>
  checkInDate?: string
  duration: number
  offer: App.VillaOffer
}

function VillasPageStickyFooter(props: Props) {
  const {
    duration,
    packageView,
    durationLabel,
    checkInDate,
    rooms,
    offer,
  } = props

  const [price, value, discount] = useVillasPrice(packageView)
  const availableForPurchase = useIsAvailableForPurchase()
  const dispatch = useOfferPageDispatch()
  const showPrices = packageView.capacityValid && packageView.hotelAvailable && availableForPurchase
  const canBook = !!(checkInDate && rooms && availableForPurchase)
  const onGuestsClick = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [dispatch])

  const onDatesClick = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [dispatch])

  const dateText = useMemo(() => {
    if (checkInDate) {
      const checkIn = moment(checkInDate)
      const checkOut = checkIn.clone().add(duration, 'days')
      if (checkIn.isSame(checkOut, 'month')) {
        return `${checkIn.format(DAY_FORMAT)} - ${checkOut.format(DAY_MONTH_NAME_SHORT)}`
      } else {
        return `${checkIn.format(DAY_MONTH_NAME_SHORT)} - ${checkOut.format(DAY_MONTH_NAME_SHORT)}`
      }
    }
  }, [checkInDate, duration])

  const pointsEarnCalculationOptions = useMemo(
    () => [generateLoyaltyPointsCalculatorVillasOptions(offer, {
      pkg: packageView.package,
      price: packageView.hotelPrice,
      memberPrice: packageView.hotelMemberPrice,
      propertyFees: packageView.propertyFees,
    })],
    [offer, packageView],
  )

  return <Footer>
    <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
      <div>
        {showPrices && <>
          {!canBook && <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>}
          {canBook && <Group direction="horizontal" gap={4}>
            <FilterChip size="small" onClick={onGuestsClick}>{formatOccupantsAsGuests(...rooms)}</FilterChip>
            <FilterChip size="small" onClick={onDatesClick}>{dateText}</FilterChip>
          </Group>}
          <LuxPlusPriceGuard
            showMemberPrice={price.member > 0}
            fallbackPrice={<PriceRowPrice
              size="S"
              price={price.hotel}
              saleUnit="total"
            />}
          >
            <PriceRowLuxPlusPrice
              size="S"
              memberPrice={price.member}
              saleUnit="total"
              logoPlacement="leading"
            />
          </LuxPlusPriceGuard>
          {!!value && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            size="L"
            originalValue={value}
            discountPercentage={discount}
          />}
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationOptions}
            calculationType={canBook ? 'exact' : 'estimate'}
          />
        </>}
      </div>
      <OfferBookButton
        packageView={packageView}
      >
        {canBook ? 'Book now' : 'Check availability'}
      </OfferBookButton>
    </Group>
  </Footer>
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    offer: state.offer,
    packageView: getCheapestSelectedPackageOptionWithDefault(appState, state),
    durationLabel: getFromDurationLabel(state, appState),
    checkInDate: state.checkInDate,
    rooms: state.rooms,
    duration: state.duration,
  }
}, VillasPageStickyFooter)
